'use client'

import { useEffect, useRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  HStack,
  Progress,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { Control, FieldErrors } from 'react-hook-form'
import type { IQuestion } from '@/entities/questionnaire/types'
import type { SurveyFormValues } from '../model/use-medical-survey-form'
import { AppCard } from '@/shared'
import { readSurveyDraft, writeSurveyDraft } from '../model/survey-draft'
import { QuestionInput } from './QuestionInput'
import { RequiredMark } from './RequiredMark'
import { SurveyConfirmation } from './SurveyConfirmation'

export interface SurveyStepItem {
  question: IQuestion
  categoryLabel: string
}

interface Props {
  items: SurveyStepItem[]
  control: Control<SurveyFormValues>
  errors: FieldErrors<SurveyFormValues>
  isPending: boolean
  /** localStorage key of the draft, so the stepper resumes on the same question. */
  draftKey: string | null
  onCancel?: () => void
}

/**
 * Walks the donor through the questionnaire one question at a time — answer the
 * question, press "next", the previous question is left behind. The final step
 * shows the completion message plus the confirmation checkbox and save button.
 */
export function SurveyStepper({
  items,
  control,
  errors,
  isPending,
  draftKey,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const total = items.length

  const [index, setIndex] = useState(() => {
    const saved = readSurveyDraft(draftKey)?.questionIndex ?? 0
    return Math.min(Math.max(saved, 0), total)
  })

  // Keep the resume point in the draft alongside the answers.
  useEffect(() => {
    writeSurveyDraft(draftKey, { questionIndex: index })
  }, [draftKey, index])

  // `index === total` is the completion step.
  const current = items.at(index)
  const finished = index >= total

  const values = useWatch({ control })

  // Once the survey has been walked through, revisiting a question is a fix-up:
  // moving on returns to the summary instead of replaying the whole tail.
  const [reviewing, setReviewing] = useState(false)
  useEffect(() => {
    if (finished) setReviewing(true)
  }, [finished])

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAdvance = () => {
    if (advanceTimer.current === null) return
    clearTimeout(advanceTimer.current)
    advanceTimer.current = null
  }

  useEffect(() => clearAdvance, [])

  const goTo = (next: number) => {
    clearAdvance()
    setIndex(Math.min(Math.max(next, 0), total))
  }

  const answerOf = (question: IQuestion): unknown =>
    (values as Record<string, unknown>)[question.questionId]

  const isAnswered = (question: IQuestion) => {
    const value = answerOf(question)
    return value !== '' && value !== undefined && value !== null
  }

  // A required question holds the flow until it is answered.
  const isBlocking = (question: IQuestion) =>
    !!question.required && !isAnswered(question)

  const blocked = !!current && isBlocking(current.question)
  const missingRequired = items.some(({ question }) => isBlocking(question))

  const goForward = () => {
    if (blocked) return
    goTo(reviewing ? total : index + 1)
  }

  // Picking a yes/no answer moves on by itself — the short delay lets the radio
  // render as selected first, and leaves room to correct a misclick. The pick
  // itself is the answer, so this path never has to wait on `blocked`.
  const goForwardSoon = () => {
    clearAdvance()
    advanceTimer.current = setTimeout(
      () => goTo(reviewing ? total : index + 1),
      350,
    )
  }

  const formatAnswer = (question: IQuestion) => {
    const value = answerOf(question)
    if (!isAnswered(question)) return '—'
    if (question.questionType === 'BOOLEAN') {
      return String(value) === 'true' ? t('common.yes') : t('common.no')
    }
    return String(value)
  }

  // Enter inside a question moves on instead of submitting the half-filled form.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || finished) return
    const tag = (event.target as HTMLElement).tagName
    if (tag === 'TEXTAREA' || tag === 'BUTTON') return
    event.preventDefault()
    goForward()
  }

  return (
    <VStack gap={5} align="stretch" onKeyDown={handleKeyDown}>
      <AppCard.Root>
        <AppCard.Body p={{ base: 5, md: 8 }}>
          {finished ? (
            <VStack gap={4} align="stretch">
              <Box
                bg="badge.green.20"
                borderWidth="1px"
                borderColor="badge.green.100"
                borderRadius="xl"
                px={4}
                py={3}
              >
                <Text fontSize="md" fontWeight="medium" color="badge.green.100">
                  {t('donor_detail.medical_survey.stepper.finished')}
                </Text>
              </Box>

              {total > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    {t('donor_detail.medical_survey.stepper.summary')}
                  </Text>
                  <VStack gap={0} align="stretch" divideY="1px">
                    {items.map(({ question }, i) => (
                      <HStack
                        key={question.questionId}
                        justify="space-between"
                        align="flex-start"
                        gap={4}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: 'bg.muted' }}
                        onClick={() => goTo(i)}
                      >
                        <Text fontSize="sm" lineHeight={1.5}>
                          {question.questionText}
                          {question.required && <RequiredMark />}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          whiteSpace="nowrap"
                        >
                          {formatAnswer(question)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          ) : (
            current && (
              <VStack gap={4} align="stretch">
                <HStack justify="space-between" gap={4}>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    {current.categoryLabel}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                    {t('donor_detail.medical_survey.stepper.progress', {
                      current: index + 1,
                      total,
                    })}
                  </Text>
                </HStack>

                <Progress.Root
                  value={index}
                  max={total}
                  size="sm"
                  colorPalette="red"
                >
                  <Progress.Track borderRadius="full">
                    <Progress.Range borderRadius="full" />
                  </Progress.Track>
                </Progress.Root>

                <Text fontSize="lg" fontWeight="medium" lineHeight={1.5}>
                  {current.question.questionText}
                  {current.question.required && <RequiredMark />}
                </Text>

                <QuestionInput
                  key={current.question.questionId}
                  question={current.question}
                  control={control}
                  size="lg"
                  onPick={goForwardSoon}
                />

                {blocked && (
                  <Text fontSize="sm" color="red.500">
                    {t('donor_detail.medical_survey.stepper.required_hint')}
                  </Text>
                )}

                <HStack justify="space-between" pt={2}>
                  <Button
                    type="button"
                    variant="ghost"
                    borderRadius="md"
                    onClick={() => goTo(index - 1)}
                    disabled={index === 0}
                  >
                    {t('donor_detail.medical_survey.stepper.back')}
                  </Button>
                  <Button
                    type="button"
                    bg="primary.solid"
                    color="white"
                    borderRadius="md"
                    px={8}
                    _hover={{ opacity: 0.85 }}
                    _active={{ opacity: 0.7 }}
                    onClick={goForward}
                    disabled={blocked}
                  >
                    {t('donor_detail.medical_survey.stepper.next')}
                  </Button>
                </HStack>
              </VStack>
            )
          )}
        </AppCard.Body>
      </AppCard.Root>

      {finished && (
        <>
          {missingRequired && (
            <Text fontSize="sm" color="red.500">
              {t('donor_detail.medical_survey.stepper.required_missing')}
            </Text>
          )}

          <SurveyConfirmation control={control} errors={errors} />

          <HStack justify="flex-end" gap={3}>
            <Button
              type="submit"
              bg="primary.solid"
              color="white"
              borderRadius="md"
              px={8}
              _hover={{ opacity: 0.85 }}
              _active={{ opacity: 0.7 }}
              disabled={isPending || missingRequired}
            >
              {isPending ? (
                <Spinner size="xs" color="white" />
              ) : (
                t('donor_detail.medical_survey.stepper.finish')
              )}
            </Button>
            {total > 0 && (
              <Button
                type="button"
                variant="outline"
                borderRadius="md"
                color={{ _dark: 'white', _light: 'fg' }}
                px={8}
                onClick={() => goTo(total - 1)}
                disabled={isPending}
              >
                {t('donor_detail.medical_survey.stepper.back')}
              </Button>
            )}
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                borderRadius="md"
                color={{ _dark: 'white', _light: 'fg' }}
                px={8}
                onClick={onCancel}
                disabled={isPending}
              >
                {t('donor_detail.medical_survey.cancel')}
              </Button>
            )}
          </HStack>
        </>
      )}
    </VStack>
  )
}
